import React, { useMemo } from "react";
import Field from "../../sections/shared/Field";
import EditorGroup from "../../sections/shared/EditorGroup";

function normalizeCtaTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

export default function CTADesignSection({
  editedSection,
  t,
  flags,
  onStyleChange,
}) {
  const isCta = Boolean(flags?.isCta);
  const tplKey = useMemo(
    () => normalizeCtaTemplate(editedSection?.template),
    [editedSection?.template]
  );

  if (!isCta) return null;

  return (
    <>
      <EditorGroup
        title={t("ctaDesign.cards.title", "Cards & Panels")}
        subtitle={t(
          "ctaDesign.cards.help",
          "Control the main CTA card, panels, and internal blocks"
        )}
        defaultOpen
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label={t("ctaDesign.cardBg", "Main Card Background")}
            type="color"
            value={editedSection?.style?.ctaCardBg}
            onChange={(v) => onStyleChange("ctaCardBg", v)}
          />
          <Field
            label={t("ctaDesign.cardBorder", "Main Card Border")}
            type="color"
            value={editedSection?.style?.ctaCardBorder}
            onChange={(v) => onStyleChange("ctaCardBorder", v)}
          />

          <Field
            label={t("ctaDesign.panelBg", "Panel Background")}
            type="color"
            value={editedSection?.style?.ctaPanelBg}
            onChange={(v) => onStyleChange("ctaPanelBg", v)}
          />
          <Field
            label={t("ctaDesign.panelBorder", "Panel Border")}
            type="color"
            value={editedSection?.style?.ctaPanelBorder}
            onChange={(v) => onStyleChange("ctaPanelBorder", v)}
          />

          <Field
            label={t("ctaDesign.footerBarBg", "Footer Bar Background")}
            type="color"
            value={editedSection?.style?.ctaFooterBarBg}
            onChange={(v) => onStyleChange("ctaFooterBarBg", v)}
          />
          <Field
            label={t("ctaDesign.footerBarText", "Footer Bar Text")}
            type="color"
            value={editedSection?.style?.ctaFooterTextColor}
            onChange={(v) => onStyleChange("ctaFooterTextColor", v)}
          />
        </div>
      </EditorGroup>

      <EditorGroup
        title={t("ctaDesign.form.title", "Form & Inputs")}
        subtitle={t(
          "ctaDesign.form.help",
          "Control labels, inputs, helper text, and success state"
        )}
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label={t("ctaDesign.inputBg", "Input Background")}
            type="color"
            value={editedSection?.style?.ctaInputBg}
            onChange={(v) => onStyleChange("ctaInputBg", v)}
          />
          <Field
            label={t("ctaDesign.inputBorder", "Input Border")}
            type="color"
            value={editedSection?.style?.ctaInputBorder}
            onChange={(v) => onStyleChange("ctaInputBorder", v)}
          />
          <Field
            label={t("ctaDesign.inputText", "Input Text")}
            type="color"
            value={editedSection?.style?.ctaInputText}
            onChange={(v) => onStyleChange("ctaInputText", v)}
          />
          <Field
            label={t("ctaDesign.inputLabel", "Input Label")}
            type="color"
            value={editedSection?.style?.ctaInputLabelColor}
            onChange={(v) => onStyleChange("ctaInputLabelColor", v)}
          />
          <Field
            label={t("ctaDesign.mutedText", "Muted / Helper Text")}
            type="color"
            value={editedSection?.style?.ctaMutedTextColor}
            onChange={(v) => onStyleChange("ctaMutedTextColor", v)}
          />
          <Field
            label={t("ctaDesign.successTitle", "Success Title")}
            type="color"
            value={editedSection?.style?.ctaSuccessTitleColor}
            onChange={(v) => onStyleChange("ctaSuccessTitleColor", v)}
          />
          <Field
            label={t("ctaDesign.successSubtitle", "Success Subtitle")}
            type="color"
            value={editedSection?.style?.ctaSuccessSubtitleColor}
            onChange={(v) => onStyleChange("ctaSuccessSubtitleColor", v)}
          />
          <Field
            label={t("ctaDesign.successIconBg", "Success Icon Background")}
            type="color"
            value={editedSection?.style?.ctaSuccessIconBg}
            onChange={(v) => onStyleChange("ctaSuccessIconBg", v)}
          />
          <Field
            label={t("ctaDesign.successIcon", "Success Icon")}
            type="color"
            value={editedSection?.style?.ctaSuccessIconColor}
            onChange={(v) => onStyleChange("ctaSuccessIconColor", v)}
          />
        </div>
      </EditorGroup>

      <EditorGroup
        title={t("ctaDesign.info.title", "Info Cards / Content Blocks")}
        subtitle={t(
          "ctaDesign.info.help",
          "Control small cards, notes, metrics, and other CTA detail blocks"
        )}
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label={t("ctaDesign.infoCardBg", "Info Card Background")}
            type="color"
            value={editedSection?.style?.ctaInfoCardBg}
            onChange={(v) => onStyleChange("ctaInfoCardBg", v)}
          />
          <Field
            label={t("ctaDesign.infoCardBorder", "Info Card Border")}
            type="color"
            value={editedSection?.style?.ctaInfoCardBorder}
            onChange={(v) => onStyleChange("ctaInfoCardBorder", v)}
          />
          <Field
            label={t("ctaDesign.infoTitle", "Info Title")}
            type="color"
            value={editedSection?.style?.ctaInfoTitleColor}
            onChange={(v) => onStyleChange("ctaInfoTitleColor", v)}
          />
          <Field
            label={t("ctaDesign.infoText", "Info Text")}
            type="color"
            value={editedSection?.style?.ctaInfoTextColor}
            onChange={(v) => onStyleChange("ctaInfoTextColor", v)}
          />
          <Field
            label={t("ctaDesign.infoNote", "Info Note")}
            type="color"
            value={editedSection?.style?.ctaInfoNoteColor}
            onChange={(v) => onStyleChange("ctaInfoNoteColor", v)}
          />
          <Field
            label={t("ctaDesign.accentBg", "Accent Block Background")}
            type="color"
            value={editedSection?.style?.ctaAccentBg}
            onChange={(v) => onStyleChange("ctaAccentBg", v)}
          />
          <Field
            label={t("ctaDesign.accentText", "Accent Block Text")}
            type="color"
            value={editedSection?.style?.ctaAccentText}
            onChange={(v) => onStyleChange("ctaAccentText", v)}
          />
          <Field
            label={t("ctaDesign.accentBorder", "Accent Block Border")}
            type="color"
            value={editedSection?.style?.ctaAccentBorder}
            onChange={(v) => onStyleChange("ctaAccentBorder", v)}
          />
        </div>
      </EditorGroup>

      {(tplKey === "template2" || tplKey === "template3") && (
        <EditorGroup
          title={t("ctaDesign.tabs.title", "Tabs / Switchers")}
          subtitle={t(
            "ctaDesign.tabs.help",
            "Control the tab bar or top CTA switcher colors"
          )}
          defaultOpen={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("ctaDesign.tabActiveBg", "Active Tab Background")}
              type="color"
              value={editedSection?.style?.ctaTabActiveBg}
              onChange={(v) => onStyleChange("ctaTabActiveBg", v)}
            />
            <Field
              label={t("ctaDesign.tabActiveText", "Active Tab Text")}
              type="color"
              value={editedSection?.style?.ctaTabActiveText}
              onChange={(v) => onStyleChange("ctaTabActiveText", v)}
            />
            <Field
              label={t("ctaDesign.tabInactiveBg", "Inactive Tab Background")}
              type="color"
              value={editedSection?.style?.ctaTabInactiveBg}
              onChange={(v) => onStyleChange("ctaTabInactiveBg", v)}
            />
            <Field
              label={t("ctaDesign.tabInactiveText", "Inactive Tab Text")}
              type="color"
              value={editedSection?.style?.ctaTabInactiveText}
              onChange={(v) => onStyleChange("ctaTabInactiveText", v)}
            />
            <Field
              label={t("ctaDesign.tabBorder", "Tab Border / Underline")}
              type="color"
              value={editedSection?.style?.ctaTabBorder}
              onChange={(v) => onStyleChange("ctaTabBorder", v)}
            />
          </div>
        </EditorGroup>
      )}

      {tplKey === "template2" && (
        <>
          <EditorGroup
            title={t("ctaDesign.badges.title", "Badges / Pills")}
            subtitle={t(
              "ctaDesign.badges.help",
              "Colors for the top badge and small CTA pills"
            )}
            defaultOpen={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t("ctaDesign.badgeBg", "Badge Background")}
                type="color"
                value={editedSection?.style?.ctaBadgeBg}
                onChange={(v) => onStyleChange("ctaBadgeBg", v)}
              />
              <Field
                label={t("ctaDesign.badgeText", "Badge Text")}
                type="color"
                value={editedSection?.style?.ctaBadgeText}
                onChange={(v) => onStyleChange("ctaBadgeText", v)}
              />
              <Field
                label={t("ctaDesign.badgeBorder", "Badge Border")}
                type="color"
                value={editedSection?.style?.ctaBadgeBorder}
                onChange={(v) => onStyleChange("ctaBadgeBorder", v)}
              />
            </div>
          </EditorGroup>

          <EditorGroup
            title={t("ctaDesign.quickItems.title", "Quick Message Items")}
            subtitle={t(
              "ctaDesign.quickItems.help",
              "Colors for WhatsApp quick template items"
            )}
            defaultOpen={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t("ctaDesign.quickItemBg", "Item Background")}
                type="color"
                value={editedSection?.style?.ctaQuickItemBg}
                onChange={(v) => onStyleChange("ctaQuickItemBg", v)}
              />
              <Field
                label={t("ctaDesign.quickItemBorder", "Item Border")}
                type="color"
                value={editedSection?.style?.ctaQuickItemBorder}
                onChange={(v) => onStyleChange("ctaQuickItemBorder", v)}
              />
              <Field
                label={t("ctaDesign.quickItemText", "Item Text")}
                type="color"
                value={editedSection?.style?.ctaQuickItemText}
                onChange={(v) => onStyleChange("ctaQuickItemText", v)}
              />
            </div>
          </EditorGroup>
        </>
      )}

      {tplKey === "template3" && (
        <EditorGroup
          title={t("ctaDesign.metrics.title", "Metrics")}
          subtitle={t(
            "ctaDesign.metrics.help",
            "Colors for the small metric cards"
          )}
          defaultOpen={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("ctaDesign.metricCardBg", "Metric Card Background")}
              type="color"
              value={editedSection?.style?.ctaMetricCardBg}
              onChange={(v) => onStyleChange("ctaMetricCardBg", v)}
            />
            <Field
              label={t("ctaDesign.metricCardBorder", "Metric Card Border")}
              type="color"
              value={editedSection?.style?.ctaMetricCardBorder}
              onChange={(v) => onStyleChange("ctaMetricCardBorder", v)}
            />
            <Field
              label={t("ctaDesign.metricValue", "Metric Value")}
              type="color"
              value={editedSection?.style?.ctaMetricValueColor}
              onChange={(v) => onStyleChange("ctaMetricValueColor", v)}
            />
            <Field
              label={t("ctaDesign.metricLabel", "Metric Label")}
              type="color"
              value={editedSection?.style?.ctaMetricLabelColor}
              onChange={(v) => onStyleChange("ctaMetricLabelColor", v)}
            />
          </div>
        </EditorGroup>
      )}
    </>
  );
}